import { casesSold } from '../../../../data/product';
import { formatCases } from '../../../../helpers/format-quantity';
import type { ProductOrderItem } from '../data/product-order-item';

type Props = {
  members: ProductOrderItem[];
  totalCases: number;
  onClickTier: (targetCases: number) => void;
  onClickDaysSupply: (
    memberCases: Array<{ member: ProductOrderItem; targetCases: number }>
  ) => void;
};

/**
 * Horizontal bar of case-count shortcut buttons above a family's SKU table.
 * Buttons are the union of distinct `min_cases` values across all members'
 * volume_discounts, sorted ascending. Click → setOrderItem across family via
 * the caller's allocator. No savings or cost in the label — those live inside
 * each SKU's row per Dirk's fat-row preference.
 */
export function FamilyTierBar({ members, totalCases, onClickTier, onClickDaysSupply }: Props) {
  const thresholds = collectThresholds(members);
  const currentTarget = pickCurrentTier(thresholds, totalCases);
  const dayTargets = [30, 60, 90, 180] as const;

  return (
    <div className="flex flex-wrap items-center justify-between gap-2">
      <div className="flex flex-wrap gap-1.5">
        {thresholds.map((t) => {
          const isCurrent = t === currentTarget;

          return (
            <button
              key={t}
              type="button"
              aria-current={isCurrent ? 'true' : undefined}
              onClick={() => onClickTier(t)}
              className={`${TIER_BUTTON_BASE} ${isCurrent ? TIER_BUTTON_CURRENT : TIER_BUTTON_IDLE}`}
            >
              {formatCases(t)}
            </button>
          );
        })}
      </div>
      <div className="ml-auto flex flex-wrap justify-end gap-1.5">
        {dayTargets.map((days) => (
          <button
            key={days}
            type="button"
            onClick={() =>
              onClickDaysSupply(
                members.map((member) => ({
                  member,
                  targetCases: nearestCasesForDays(member, days),
                }))
              )
            }
            className={`${TIER_BUTTON_BASE} ${TIER_BUTTON_IDLE}`}
          >
            {days}D
          </button>
        ))}
      </div>
    </div>
  );
}

// These controls intentionally look like the app's small outline buttons, but
// stay local because this view needs a fixed-width layout and an aria-current
// visual state for active family tiers.
const TIER_BUTTON_BASE = 'w-24 rounded border px-2 py-1.5 text-xs';
const TIER_BUTTON_CURRENT = 'border-emerald-600 bg-emerald-50 font-semibold text-emerald-700';
const TIER_BUTTON_IDLE =
  'border-gray-300 bg-white font-medium text-gray-900 hover:border-emerald-600 hover:text-emerald-700';

function collectThresholds(members: ProductOrderItem[]): number[] {
  const set = new Set<number>();
  for (const m of members) {
    const discounts = m.product.pricing?.volume_discounts ?? [];
    // Sub-1-case tiers (e.g. min_cases=0.167 = "any quantity") have no
    // sensible meaning in this case-focused allocator — clicking one would
    // ask the allocator to fill the family to a fractional case count. Hide.
    for (const d of discounts) {
      if (d.min_cases >= 1) {
        set.add(d.min_cases);
      }
    }
  }

  return [...set].sort((a, b) => a - b);
}

function pickCurrentTier(thresholds: number[], totalCases: number): number | null {
  let current: number | null = null;
  for (const t of thresholds) {
    if (t <= totalCases) {
      current = t;
    } else {
      break;
    }
  }

  return current;
}

function nearestCasesForDays(member: ProductOrderItem, days: 30 | 60 | 90 | 180): number {
  const soldCases = casesSold(member.product, days);
  if (!Number.isFinite(soldCases)) {
    return 0;
  }

  return Math.max(0, Math.round(soldCases));
}
